import React from 'react'

import { Box, Container, Grid, Hidden, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import Carousel from 'artkive/components/Carousel'
import Image from 'artkive/components/Image'
import ContainedSection from 'artkive/components/PageSections/ContainedSection'
import HowItWorks, { HowItWorksButton } from 'artkive/components/PageSections/HowItWorks'
import { ProductDescriptionItem } from 'artkive/components/PageSections/ProductDescriptionBox'
import Social from 'artkive/components/Social'
import { TrackButton } from 'artkive/components/Tracker'
import { GET_BOX_CLICK } from 'artkive/constants/tracker/mainNavigation'
import withApp from 'artkive/hoc/withApp'
import useMobileBreakpoint from 'artkive/hooks/useMobileBreakpoint'
import HeroSection from 'artkive/pages/Home/HeroSection'
import { defaultHowItWorks } from 'artkive/stores/ecom/constants/data'
import routes from 'artkive/utils/routes'
import theme from 'artkive/utils/theme'

import Bubbles from 'images/ecom/brand/bubbles.svg'
import sprite from 'images/ecom/brand/sprite.svg'
import ArtBooks from 'images/ecom/home/Home-ArtBooks.png'
import Mosaics from 'images/ecom/home/Home-Mosaics.jpg'

import { homeBookProductBullets } from '../Books/Books.constants'
import DigitizedImagesAccessSection from '../Books/DigitizedImagesAccessSection'
import GetStartedWithBoxSection from '../Books/GetStartedWithBoxSection'
import { homeMosaicsProductBullets } from '../Mosaics/Mosaics.content'

import AsFeaturedOnSection from './AsFeaturedOnSection'
import GetStartedFooter from './GetStartedFooter'
import { SLIDES } from './Home.constants'
import SeeUsInMotionSection from './SeeUsInMotionSection'
import TrustIndexReviews from './TrustIndexReviews'

const useStyles = makeStyles(() => ({
  wrapper: {
    backgroundColor: theme.colors.white.dark,
  },
  howItWorksImage: {
    maxWidth: 120,
    height: 'auto',
  },
  howItWorksDescription: {
    lineHeight: 1.6,
    fontWeight: 500,
  },
  productsGrid: {
    position: 'relative',
    zIndex: 1,
  },
}))

const useSpriteStyles = makeStyles(({ breakpoints }) => ({
  container: {
    position: 'absolute',
    zIndex: 2,
    top: -24,
    right: 4,

    [breakpoints.up('md')]: {
      top: -50,
      right: -40,
    },
  },
}))

const Sprite = () => {
  const classes = useSpriteStyles()

  return (
    <div className={classes.container}>
      <Image src={sprite} style={{ width: '75px', transform: 'scaleX(-1)' }} />
    </div>
  )
}

const Home = () => {
  const isMobile = useMobileBreakpoint()
  const classes = useStyles()

  return (
    <div className={classes.wrapper}>
      <HeroSection />
      <Box
        bgcolor={theme.colors.white.main}
        py={isMobile ? 8 : theme.spacings.section}
      >
        <Container>
          <Box mb={isMobile ? 6 : 8} textAlign={'center'}>
            <Typography variant={'h2'}>Our Products</Typography>
          </Box>
          {/* accents */}
          <Grid container spacing={5} className={classes.productsGrid}>
            <Sprite />

            <Grid xs={12} md={6} item>
              <Box position={'relative'} zIndex={1} maxWidth={'100%'}>
                <Hidden smDown>
                  <Box
                    position={'absolute'}
                    bottom={'-45px'}
                    left={'-60px'}
                    zIndex={-1}
                  >
                    <Image
                      src={Bubbles}
                      style={{ width: '95px' }}
                    />
                  </Box>
                </Hidden>
                <Image alt={'Art Books'} src={ArtBooks} title={'ArtBooks'} />
              </Box>

              <Box px={isMobile ? 0 : 12} pt={4} pb={6}>
                <Typography variant={'h3'} align={'center'}>
                  Art Books
                </Typography>
                {homeBookProductBullets.map(({ text }, index) => (
                  <ProductDescriptionItem key={index}>
                    {text}
                  </ProductDescriptionItem>
                ))}
              </Box>

              <Box
                display={'flex'}
                justifyContent={'center'}
                textAlign={'center'}
              >
                <TrackButton
                  color={'primary'}
                  href={routes.books}
                  size={'large'}
                  variant={'outlined'}
                  trackerEvent={GET_BOX_CLICK}
                  trackerOptions={{
                    section: 'get_books_banner',
                  }}
                >
                  Learn More
                </TrackButton>
              </Box>
              <Hidden mdUp>
                <Box py={4} />
              </Hidden>
            </Grid>

            <Grid xs={12} md={6} item>
              <Box position={'relative'} zIndex={1} maxWidth={'100%'}>
                <Hidden mdUp>
                  <Box
                    position={'absolute'}
                    bottom={'-35px'}
                    left={'-35px'}
                    zIndex={-1}
                  >
                    <Image
                      src={Bubbles}
                      style={{ width: '75px' }}
                    />
                  </Box>
                </Hidden>
                <Image
                  src={Mosaics}
                  alt={'Framed Mosaics'}
                  title={'Frames Mosaics'}
                />
              </Box>

              <Box px={isMobile ? 0 : 12} pt={4} pb={6}>
                <Typography variant={'h3'} align={'center'}>
                  Framed Mosaics
                </Typography>

                {homeMosaicsProductBullets.map(({ text }, index) => (
                  <ProductDescriptionItem key={index}>
                    {text}
                  </ProductDescriptionItem>
                ))}
              </Box>

              <Box display={'flex'} justifyContent={'center'}>
                <TrackButton
                  color={'primary'}
                  href={routes.mosaics}
                  size={'large'}
                  variant={'outlined'}
                  trackerEvent={GET_BOX_CLICK}
                  trackerOptions={{
                    section: 'get_books_banner',
                  }}
                >
                  Learn More
                </TrackButton>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
      {/* END our products */}

      {/* START - how it works */}
      <HowItWorks
        steps={defaultHowItWorks}
        title={'How It Works'}
        variant={'image'}
      >
        <HowItWorksButton
          title={'Get My Box'}
          href={routes.boxCheckout}
          trackerEvent={GET_BOX_CLICK}
          trackerOptions={{ section: 'home_how_it_works' }}
        />
      </HowItWorks>

      {/* END - how it works */}
      <GetStartedWithBoxSection />

      <DigitizedImagesAccessSection bgcolor={isMobile ? undefined : theme.colors.white.main} />

      {/* START - Testimonials */}
      <ContainedSection
        disableGutters
        title={
          <Typography variant={'h2'} align={'center'}>
            400,000 families, 20 million pieces of art and counting
          </Typography>
        }
      >
        <TrustIndexReviews />

        <Box justifyContent={'center'} display={'flex'} mt={isMobile ? 6 : 8}>
          <TrackButton
            color={'primary'}
            variant={'contained'}
            href={routes.boxCheckout}
            trackerEvent={GET_BOX_CLICK}
            trackerOptions={{
              section: 'home_how_it_works',
            }}
            size={'large'}
          >
            Get My Box
          </TrackButton>
        </Box>
      </ContainedSection>
      {/* END - Testimonials */}

      {/* Carousel */}
      <Container disableGutters={isMobile}>
        <Box position={'relative'} my={10} mx={4}>
          {/* accents */}
          <Box position={'absolute'} top={isMobile ? '-50px' : '-50px'} left={isMobile ? '-50px' : '-50px'} zIndex={1}>
            <Image src={sprite} style={{ width: '50px' }} />
          </Box>
          <Carousel isMobile={isMobile} slides={SLIDES} />
        </Box>
      </Container>

      {/* START featured on */}
      <AsFeaturedOnSection />
      {/* END featured on */}

      {/* SeeUsInMotion */}
      <SeeUsInMotionSection />

      {/* Join The Fun  */}
      <Social />
      <GetStartedFooter />
    </div>
  )
}

export default withApp(Home)
